import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Visibility from '@mui/icons-material/VisibilityOutlined'
import VisibilityOff from '@mui/icons-material/VisibilityOffOutlined'
import { Button, Input } from '@/design-system'
import {
  authFieldSx,
  authPasswordFieldSx,
  ErrorBanner,
  FieldError,
  FieldLabel,
  FieldWrap,
  FormPage,
  FormSub,
  FormTitle,
  FormWrap,
  // GoogleGlyph,
  LegalText,
  // OrDivider,
  PasswordFieldBox,
  PasswordToggle,
  // SsoButton,
  // SsoStack,
  SwapRow,
} from './__styled'
import { validateEmail, validatePassword, validatePhoneNumber } from '@/utils/helper'
// import { toast } from 'react-hot-toast'

interface FormProps {
  error: string
  loading: boolean
  onSubmit: (user: string, password: string) => void
}

export default function SignUpForm1({ error, loading, onSubmit }: FormProps) {
  const [user, setUser] = useState('') // Email or Phone number
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [editing, setEditing] = useState(false)

  const formError = useMemo(() => {
    const error: Record<string, string> = {}
    if (!user.length) {
      error.user = 'Email or Phone Number is required'
    } else if (!validateEmail(user) && !validatePhoneNumber(user)) {
      error.user = 'Enter Valid Phone/Email'
    }
    if (!password.length) {
      error.password = 'Password is required'
    } else if (password.length < 8) {
      error.password = 'Password is too short - should be 8 chars minimum.'
    } else if (!validatePassword(password)) {
      error.password =
        'Password can only contain Latin Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character.'
    }
    if (password !== confirmPassword) {
      error.confirm = 'Passwords must match'
    }
    return error
  }, [user, password, confirmPassword])

  const isFormValid = !formError.user && !formError.password && !formError.confirm

  const onChangeUser = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUser(e.target.value)
    setEditing(true)
  }

  const onChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
    setEditing(true)
  }

  const onChangeConfirmPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value)
    setEditing(true)
  }

  const onContinue = () => {
    if (isFormValid) onSubmit(user, password)
  }

  // const soonToast = () => toast('Coming soon', { icon: '✨' })

  return (
    <FormPage>
      <FormWrap>
        <FormTitle>Create your account</FormTitle>
        <FormSub>Free to start. Your first album is on us.</FormSub>

        {/* SSO not wired yet — restore with Google/Apple when ready
        <SsoStack>
          <SsoButton type='button' onClick={soonToast}>
            <GoogleGlyph /> Continue with Google
          </SsoButton>
          <SsoButton type='button' onClick={soonToast}>
            <span style={{ fontSize: 16 }}></span> Continue with Apple
          </SsoButton>
        </SsoStack>

        <OrDivider>OR</OrDivider>
        */}

        <FieldWrap>
          <FieldLabel>Email or phone</FieldLabel>
          <Input
            variant='filled'
            sx={authFieldSx}
            value={user}
            placeholder='elena@example.com'
            onChange={onChangeUser}
            name='email'
          />
          {editing && !!formError.user && <FieldError>{formError.user}</FieldError>}
        </FieldWrap>

        <FieldWrap>
          <FieldLabel>Create password</FieldLabel>
          <PasswordFieldBox>
            <Input
              variant='filled'
              sx={authPasswordFieldSx}
              value={password}
              placeholder='At least 8 characters'
              onChange={onChangePassword}
              type={showPassword ? 'text' : 'password'}
              name='password'
            />
            <PasswordToggle type='button' onClick={() => setShowPassword((v) => !v)}>
              {showPassword ? <VisibilityOff fontSize='small' /> : <Visibility fontSize='small' />}
            </PasswordToggle>
          </PasswordFieldBox>
          {editing && !!formError.password && <FieldError>{formError.password}</FieldError>}
        </FieldWrap>

        <FieldWrap>
          <FieldLabel>Confirm password</FieldLabel>
          <PasswordFieldBox>
            <Input
              variant='filled'
              sx={authPasswordFieldSx}
              value={confirmPassword}
              placeholder='Type it again'
              onChange={onChangeConfirmPassword}
              type={showConfirmPassword ? 'text' : 'password'}
              name='confirm-password'
            />
            <PasswordToggle type='button' onClick={() => setShowConfirmPassword((v) => !v)}>
              {showConfirmPassword ? (
                <VisibilityOff fontSize='small' />
              ) : (
                <Visibility fontSize='small' />
              )}
            </PasswordToggle>
          </PasswordFieldBox>
          {editing && !!formError.confirm && <FieldError>{formError.confirm}</FieldError>}
        </FieldWrap>

        {!loading && !!error && (
          <ErrorBanner>
            <FieldError sx={{ mt: 0 }}>{error}</FieldError>
          </ErrorBanner>
        )}

        <Button variant='spectrum' size='lg' fullWidth disabled={!isFormValid} loading={loading} onClick={onContinue}>
          Continue →
        </Button>

        <LegalText>
          By continuing you agree to the{' '}
          <a href='/terms' target='_blank' rel='noopener noreferrer'>Terms</a> and{' '}
          <a href='/privacy' target='_blank' rel='noopener noreferrer'>Privacy Policy</a>. Your
          photos are yours — full stop.
        </LegalText>
        <SwapRow>
          Already have an account? <Link to='/login'>Sign in</Link>
        </SwapRow>
      </FormWrap>
    </FormPage>
  )
}
